import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { SelectComponent } from "@/components/select/Select";
import { ModalidadDosisServicio } from "@/constants/enums";
import { AppColors } from "@/styles/colors";
import { handleTonChangeInInput } from "@/utils/query-string";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import { Button, Card, Field, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { Trash2 } from "lucide-react";
import { Control, FieldErrors } from "react-hook-form";
import { ServiceMasterCreationForm } from "../create/schema/service-master-creation.schema";

const modalidadOptions = [
  { label: "Fija", value: ModalidadDosisServicio.Fija },
  { label: "Variable", value: ModalidadDosisServicio.Variable },
];

interface Props {
  index: number;
  control: Control<ServiceMasterCreationForm>;
  errors: FieldErrors<ServiceMasterCreationForm>;
  dosisFieldsLength: number;
  loading?: boolean;
  removeDosisRange: (index: number) => void;
  handleAddDosisRange: () => void;
}

export const ServiceMasterDosisRangeCard = ({
  index,
  control,
  errors,
  dosisFieldsLength,
  loading,
  removeDosisRange,
  handleAddDosisRange,
}: Props) => {
  const disableDelete = dosisFieldsLength <= 1 || loading;
  const showDelete = dosisFieldsLength > 1;
  const rowErrors = errors?.cotizacionesDosis?.[index];

  return (
    <Card.Root maxW="full" borderRadius="8px">
      <Card.Body
        paddingX="32px"
        paddingY="24px"
        borderRadius="8px"
        border={`1px solid ${AppColors.BLUE_TEXT}`}
      >
        <Flex justifyContent="space-between" alignItems="center" marginBottom="16px">
          <Text fontWeight={700} fontSize="16px">
            {`Rango ${index + 1}`}
          </Text>

          {showDelete && (
            <Trash2
              cursor={disableDelete ? "not-allowed" : "pointer"}
              style={{ pointerEvents: disableDelete ? "none" : "auto" }}
              onClick={() => {
                if (!disableDelete) removeDosisRange(index);
              }}
              aria-label="Eliminar rango"
              data-testid={`service-master-remove-dosis-${index}`}
            />
          )}
        </Flex>

        <Grid templateColumns="repeat(4, 1fr)" gap="16px" alignItems="end">
          <GridItem>
            <Field.Root invalid={!!rowErrors?.modalidad}>
              <SelectComponent
                name={`cotizacionesDosis.${index}.modalidad`}
                items={modalidadOptions}
                labelText="Modalidad"
                placeholder="Seleccionar"
                control={control as any}
                clearable
                disabled={loading}
                minHeight="40px"
                required
              />
              <Field.ErrorText>
                {String(rowErrors?.modalidad?.message ?? "")}
              </Field.ErrorText>
            </Field.Root>
          </GridItem>

          <GridItem>
            <InputField
              inputName={`cotizacionesDosis.${index}.dosisDesde`}
              labelText="Dosis Desde"
              placeholder="0"
              control={control as any}
              disabled={loading}
              type="text"
              required
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
              minHeight="40px"
            />
          </GridItem>

          <GridItem>
            <InputField
              inputName={`cotizacionesDosis.${index}.dosisHasta`}
              labelText="Dosis Hasta"
              placeholder="0"
              control={control as any}
              disabled={loading}
              type="text"
              required
              allowedPattern={numbersWithCommaDecimal}
              onChange={handleTonChangeInInput}
              minHeight="40px"
            />
          </GridItem>

          <GridItem>
            <Field.Root invalid={!!rowErrors?.valorUnitario}>
              <Field.Label>
                <Text fontWeight={600}>
                  Valor Unitario
                  <Text as="span" color={AppColors.REQUIRED}>
                    {" *"}
                  </Text>
                </Text>
              </Field.Label>
              <InputCurrency
                control={control as any}
                name={`cotizacionesDosis.${index}.valorUnitario`}
                height="40px"
                disabled={loading}
              />
              <Field.ErrorText>
                {String(rowErrors?.valorUnitario?.message ?? "")}
              </Field.ErrorText>
            </Field.Root>
          </GridItem>
        </Grid>

        {index === dosisFieldsLength - 1 && (
          <Flex marginTop="20px">
            <Button
              type="button"
              onClick={handleAddDosisRange}
              disabled={loading}
              paddingX="12px"
              height="32px"
              borderRadius="6px"
              bgColor={AppColors.GREEN_PRIMARY}
              opacity={loading ? 0.6 : 1}
              data-testid="service-master-add-dosis-range"
            >
              <Text fontWeight={600} fontSize="14px" color="white">
                Guardar y agregar otra dosis
              </Text>
            </Button>
          </Flex>
        )}
      </Card.Body>
    </Card.Root>
  );
};
